// Language Context - provides i18n support across the app
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language, languages } from './translations-meta';
import type { TranslationKey } from './translations';
import { detectLanguageFromPath, getEquivalentPath, getRouteKeyFromPath, RouteKey } from './routes';

// Start loading translations immediately (module-level, not inside component)
// This means the download begins as soon as this module is first imported
type TranslationsMap = Record<string, Record<string, string>>;
let translationsCache: TranslationsMap | null = null;
const translationsPromise = import('./translations').then(mod => {
  translationsCache = mod.translations as unknown as TranslationsMap;
});

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  switchLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  
  // Map common language codes
  const langMap: Record<string, Language> = {
    en: 'en',
    es: 'es',
    de: 'de',
    fr: 'fr',
    nl: 'nl',
    it: 'it',
    pt: 'pt',
    da: 'dk',
  };

  return langMap[browserLang] || 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(!!translationsCache);

  const [language, setLanguageState] = useState<Language>(() => {
    // First check URL for language prefix
    const urlLang = detectLanguageFromPath(window.location.pathname);
    if (urlLang) {
      return urlLang;
    }
    
    // Then check localStorage
    const stored = localStorage.getItem('i-rent-language') as Language;
    if (stored && languages.includes(stored)) {
      return stored;
    }
    
    // Finally detect from browser
    return detectBrowserLanguage();
  });

  // Sync language from URL changes
  useEffect(() => {
    const urlLang = detectLanguageFromPath(location.pathname);
    if (urlLang && urlLang !== language) {
      setLanguageState(urlLang);
      localStorage.setItem('i-rent-language', urlLang);
    } else if (!urlLang && language !== 'en') {
      // URL has no prefix but language isn't English
      // This means user navigated to an English URL, update language
      setLanguageState('en');
      localStorage.setItem('i-rent-language', 'en');
    }
  }, [location.pathname]);

  // Load translations
  useEffect(() => {
    if (!translationsCache) {
      translationsPromise.then(() => setReady(true));
    }
  }, []);

  // Update document lang attribute
  useEffect(() => {
    document.documentElement.lang = language === 'dk' ? 'da' : language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('i-rent-language', lang);
    document.documentElement.lang = lang === 'dk' ? 'da' : lang;
  };

  // Switch language and navigate to equivalent URL
  const switchLanguage = (newLang: Language) => {
    if (newLang === language) return;
    
    const equivalentPath = getEquivalentPath(location.pathname, newLang);
    setLanguage(newLang);
    navigate(equivalentPath);
  };

  const t = useCallback((key: TranslationKey): string => {
    if (!translationsCache) return '';
    return (translationsCache[language]?.[key as string] || translationsCache.en?.[key as string] || key) as string;
  }, [language]);

  // Show minimal loader while translations are loading (typically <100ms)
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  // Defensive fallback: in rare HMR/refresh edge-cases the provider can be
  // temporarily unavailable. Avoid a hard crash + blank screen.
  if (context === undefined) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('useLanguage used outside LanguageProvider; falling back to en');
    }

    return {
      language: 'en' as Language,
      setLanguage: () => {
        /* noop */
      },
      switchLanguage: () => {
        /* noop */
      },
      t: (key: TranslationKey) => {
        if (!translationsCache) return key as string;
        return (translationsCache.en?.[key as string] || key) as string;
      },
    };
  }

  return context;
}
